import { Instagram, Linkedin, Mail, Facebook, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { CONTACT } from "../lib/constants";

export default function Footer() {
  return (
    <footer
      className="relative pt-20 pb-10 bg-black overflow-hidden"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-12 pb-14 border-b border-white/8">
          <div className="md:col-span-5">
            <Logo size="text-3xl" />
            <p className="mt-5 text-sm text-zinc-400 font-body max-w-sm">
              Turn visibility into impact — smart surfaces for modern spaces.
            </p>
            <a
              href={`mailto:${CONTACT.email}`}
              className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-cyan-400 transition font-body"
              data-testid="footer-email"
            >
              <Mail size={16} />
              {CONTACT.email}
            </a>
            <div
              className="mt-3 inline-flex items-start gap-2 text-xs text-zinc-500 font-body"
              data-testid="footer-address"
            >
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span>
                {CONTACT.company.address}
                <br />
                {CONTACT.company.region}
              </span>
            </div>
            <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-zinc-600 font-body">
              {CONTACT.company.name} · {CONTACT.company.vat}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-5">
              Oplossingen
            </div>
            <ul className="space-y-3 text-sm text-zinc-300 font-body">
              <li>
                <a
                  href="/#privacy"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-link-privacy-product"
                >
                  Privacy Smart Film
                </a>
              </li>
              <li>
                <a
                  href="/#led"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-link-led"
                >
                  LED Display Film
                </a>
              </li>
              <li>
                <a
                  href="/#panels"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-link-panels"
                >
                  Smart Wall Panels
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-5">
              Juridisch
            </div>
            <ul className="space-y-3 text-sm text-zinc-300 font-body">
              <li>
                <Link
                  to="/privacy"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-privacy-link"
                >
                  Privacybeleid
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-cookies-link"
                >
                  Cookiebeleid
                </Link>
              </li>
              <li>
                <Link
                  to="/voorwaarden"
                  className="hover:text-cyan-400 transition"
                  data-testid="footer-terms-link"
                >
                  Algemene voorwaarden
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-5">
              Volg JYNOVA
            </div>
            <div className="flex items-center gap-3">
              <SocialLink
                href={CONTACT.socials.instagram}
                label="Instagram"
                testid="social-instagram"
              >
                <Instagram size={17} />
              </SocialLink>
              <SocialLink
                href={CONTACT.socials.facebook}
                label="Facebook"
                testid="social-facebook"
              >
                <Facebook size={17} />
              </SocialLink>
              <SocialLink
                href={CONTACT.socials.linkedin}
                label="LinkedIn"
                testid="social-linkedin"
              >
                <Linkedin size={17} />
              </SocialLink>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="py-14 overflow-hidden">
          <div
            className="jn-massive text-center select-none"
            style={{ fontSize: "clamp(4rem, 18vw, 16rem)" }}
            aria-hidden="true"
            data-testid="footer-wordmark"
          >
            JYNOVA
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-500 font-body">
          <div>© 2026 {CONTACT.company.name}. All rights reserved.</div>
          <div className="uppercase tracking-[0.25em]">
            Smart surfaces for modern spaces
          </div>
        </div>
      </div>
    </footer>
  );
}

function SocialLink({ href, label, children, testid }) {
  const external = href && href.startsWith("http");
  return (
    <a
      href={href}
      aria-label={label}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="w-10 h-10 rounded-full border border-white/10 text-zinc-300 hover:text-cyan-400 hover:border-cyan-400/50 flex items-center justify-center transition"
      data-testid={testid}
    >
      {children}
    </a>
  );
}
